import type { AxiosInstance } from 'axios';
import authService from './auth.service';
import type { FundableProduct, FundingActivity, FundInstructions } from '../types/finance';

/**
 * Fund-your-account API client. Renamed from `paymentInstructions.service`
 * on 2026-05-24 — client copy uses "fund" rather than "payment" so the
 * action reads as wealth-building, not bill-paying.
 *
 * Backend endpoints:
 *   GET /api/v1/clients/{client_id}/fund-instructions
 *   GET /api/v1/clients/{client_id}/funding-activity
 *   PUT /api/v1/clients/{client_id}/products/{product_id}/funding-reference
 */
class FundService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async getFundInstructions(clientId: string): Promise<FundInstructions> {
    const res = await this.api.get<FundInstructions>(
      `/api/v1/clients/${clientId}/fund-instructions`,
    );
    return res.data;
  }

  /** Issue — or return, when it already exists — the client's permanent
   *  funding reference for a contracted product. Idempotent server-side. */
  async issueFundingReference(clientId: string, productId: string): Promise<FundableProduct> {
    const res = await this.api.put<FundableProduct>(
      `/api/v1/clients/${clientId}/products/${productId}/funding-reference`,
    );
    return res.data;
  }

  /** Recent transfers as lifecycle stages (awaiting → received →
   *  confirmed). Powers the funding-activity timeline so a sent wire
   *  doesn't silently vanish from the client's view. */
  async getFundingActivity(clientId: string): Promise<FundingActivity> {
    const res = await this.api.get<FundingActivity>(
      `/api/v1/clients/${clientId}/funding-activity`,
    );
    return res.data;
  }
}

export default new FundService();
